import * as React from 'react';
import {
    useLocation,
    useNavigate
} from 'react-router-dom';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ListItemButton from '@mui/material/ListItemButton';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import useTheme from '@mui/material/styles/useTheme';
import {
    useQuery,
    UseQueryResult
} from 'react-query';
import {
    ReactComponent as ClassesIcon
} from 'icons/classes.svg';
import {
    ReactComponent as EducatorResourcesIcon
} from 'icons/educatorResources.svg';
import {
    ReactComponent as ReportsIcon
} from 'icons/report.svg';
import {
    ReactComponent as HelpIcon
} from 'icons/help.svg';
import {
    CLASS_VIEW_STUDENT_GUIDE,
    CLASS_VIEW_TEACHER_GUIDE,
    TEACHERS_GUIDE_LINK
} from 'config/config';
import IClass from 'models/IClass';
import { HttpClient } from 'apiclient/ApiClient';
import TeacherGuide from '../classes-components/TeacherGuide';
import StudentGuide from '../classes-components/StudentGuide';
import { useAuth } from 'auth/AuthProvider';

import Dialog from '@mui/material/Dialog';
import PopupWithCloseButton from 'ui/components/classes-components/PopupWithCloseButton';




const Collapsable = (
    props: {
        children: JSX.Element | JSX.Element[],
        header: string,
        callback: () => void,
        isOpen?: boolean,
        icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & {
            title?: string | undefined;
        }>
    }
) => {
    const [open, setOpen] = React.useState(true);
    const loc = useLocation();
    const theme = useTheme();
    const [col, setCol] = React.useState(theme.palette.secondary.contrastText);
    React.useEffect(() => {
        if (props.header.toLowerCase() === loc.pathname.split('/').pop()) {
            setCol(theme.palette.primary.main);
        } else {
            setCol(theme.palette.secondary.contrastText);
        };
    }, [loc.pathname, props.header, theme.palette]);
    const handleCollapseClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(!open);
    };
    const handleHeaderClick = () => {
        props.callback();
    };
    return (
        <>
            <Box display='flex' sx={{ alignItems: 'center', pr: 2 }}>
                <ListItemButton
                    sx={{ px: 3, height: 64, color: col }}
                    onClick={handleHeaderClick}
                >
                    {props.icon && <props.icon fill={col} />}
                    <ListItemText primary={props.header} sx={props.icon ? { ml: 2 } : {}} />
                </ListItemButton>
                <IconButton onClick={handleCollapseClick}>
                    {
                        open ?
                        <ArrowDropUp color='secondary' /> :
                        <ArrowDropDown color='secondary' />
                    }
                </IconButton>
            </Box>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {props.children}
                </List>
            </Collapse>
        </>
    );
};

const ListMenu = (props: { callback?: () => void }) => {
    const navigate = useNavigate();
    const loc = useLocation();
    const theme = useTheme();
    const httpClient = HttpClient();
    // const { isManager, isTeacher, user } = useAuth();
    const { session } = useAuth();
    const classesGet: UseQueryResult<IClass[]> = useQuery(
        'classes', async () => await httpClient.get('/school/classes'),
        { enabled: !!session }
    );

    // Determine which section should be open based on current route
    const isClassesRoute = loc.pathname.startsWith('/classes');
    const isReportsRoute = loc.pathname.startsWith('/reports') || loc.pathname.startsWith('/organization-reports');
    const classesOpen = isClassesRoute || (!isClassesRoute && !isReportsRoute);
    const reportsOpen = isReportsRoute;

    const [popupOpen, setPopupOpen] = React.useState(false);
    const [popupElement, setPopupElement] = React.useState<null | JSX.Element>(null);
    const [popupTitle, setPopupTitle] = React.useState('');
    const closePopup = () => {
        setPopupOpen(false);
        setPopupTitle('');
        setPopupElement(null);
    };

    // Don't render menu items until session is loaded
    if (!session) {
        return <List sx={{ pt: 0 }} />;
    }

    return (
        <List sx={{ pt: 0 }}>
            {/* Classes section - only for teachers */}
            {(
                <Collapsable
                    header='Classes'
                    callback={() => {
                        props.callback && props.callback();
                        navigate('/classes');
                    }}
                    isOpen={classesOpen}
                    icon={ClassesIcon}
                >
                    {(Array.isArray(classesGet.data) ? classesGet.data : []).sort(
                        (a, b) => (a.name).localeCompare(b.name)
                    ).map((cl, idx) => (
                        <ListItemButton
                            key={idx}
                            sx={{
                                pl: 8,
                                height: 48,
                                ...(loc.pathname.split('/').pop() === cl.id && { color: 'primary.main' })
                            }}
                            onClick={() => {
                                props.callback && props.callback();
                                navigate(`/classes/${cl.id}`);
                            }}
                        >
                            <ListItemText
                                primary={cl.name}
                                primaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItemButton>
                    )) || []}
                </Collapsable>
            )}
            
            {/* Reports section - different behavior for managers and teachers */}
            {session.role === 'manager' ? (
                // For managers: Reports button without collapsible children
                <ListItemButton
                    sx={{ px: 3, height: 64 }}
                    onClick={() => {
                        props.callback && props.callback();
                        navigate('/organization-reports');
                    }}
                >
                    <ReportsIcon fill={theme.palette.secondary.contrastText} />
                    <ListItemText primary="Reports" sx={{ ml: 2 }} />
                </ListItemButton>
            ) : session.role === 'teacher' ? (
                // For teachers: Collapsible Reports with class list
                <Collapsable
                    header='Reports'
                    callback={() => {
                        props.callback && props.callback();
                        navigate('/reports');
                    }}
                    isOpen={reportsOpen}
                    icon={ReportsIcon}
                >
                    {(Array.isArray(classesGet.data) ? classesGet.data.filter(val => val.source === 'teacher') : []).sort(
                        (a, b) => (a.name).localeCompare(b.name)
                    ).map((cl, idx) => (
                        <ListItemButton
                            key={idx}
                            sx={{
                                pl: 8,
                                height: 48,
                                ...(loc.pathname === `/reports/${cl.id}` && { color: 'primary.main' })
                            }}
                            onClick={() => {
                                props.callback && props.callback();
                                navigate(`/reports/${cl.id}`);
                            }}
                        >
                            <ListItemText
                                primary={cl.name}
                                primaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItemButton>
                    )) || []}
                </Collapsable>
            ) : null}
            <ListItemButton
                sx={{ px: 3, height: 64 }}
                onClick={() => {
                    props.callback && props.callback();
                    window.open(TEACHERS_GUIDE_LINK, '_blank')?.focus();
                }}
            >
                <EducatorResourcesIcon fill={theme.palette.secondary.contrastText} />
                <ListItemText primary="Educator Resources" sx={{ ml: 2 }} />
            </ListItemButton>
            <ListItemButton
                sx={{ px: 3, height: 64 }}
                onClick={() => {
                    setPopupElement(<TeacherGuide />)
                    setPopupTitle(CLASS_VIEW_TEACHER_GUIDE);
                    setPopupOpen(true);
                }}
            >
                <HelpIcon fill={theme.palette.secondary.contrastText} />
                <ListItemText primary={CLASS_VIEW_TEACHER_GUIDE} sx={{ ml: 2 }} />
            </ListItemButton>
            <ListItemButton
                sx={{ px: 3, height: 64 }}
                onClick={() => {
                    setPopupElement(<StudentGuide />)
                    setPopupTitle(CLASS_VIEW_STUDENT_GUIDE);
                    setPopupOpen(true);
                }}
            >
                <HelpIcon fill={theme.palette.secondary.contrastText} />
                <ListItemText primary={CLASS_VIEW_STUDENT_GUIDE} sx={{ ml: 2 }} />
            </ListItemButton>
            <div style={{ textAlign: 'center', marginTop: '40px' }}>
                <a
                    href="https://edtechimpact.com/products/funexpected-math/survey"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                    src="https://media.edtechimpact.com/shared/badges/Edtech_Impact_ReviewUsOn.png"
                    alt="EdTech Impact"
                    style={{ width: '50%', height: 'auto' }}
                    />
                </a>
            </div>


            <Dialog
                open={popupOpen}
                onClose={closePopup}
            >
                <PopupWithCloseButton
                    closePopap={closePopup}
                    title={popupTitle}
                >
                    {popupElement || <></>}
                </PopupWithCloseButton>
            </Dialog>
        </List>
    )
};

export default ListMenu;